import { PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader } from 'antd';
import React from 'react';
import { DRAWER_WIDTH } from '../../config/constants';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { translations } from '../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../core/models/enum';
import { authorizeAction } from '../../helpers/CheckPermissionHelper';
import { getNewRoute } from '../../helpers/RoutingHelper';
import { ExpenseGroupsClient, ExpenseGroupVm } from '../../utils/api';
import ExpenseGroupForm from './expense-group-form';
import ExpenseGroupTable from './expense-group-table';
import { Props } from './index';

interface State {
    allowCreate: boolean;
    selectedExpenseGroup?: ExpenseGroupVm;
    drawerState: DrawerState;
}

class ExpenseGroups extends React.Component<Props, State> {
    private tableRef: any = React.createRef();

    public constructor(props: Props) {
        super(props);

        this.state = {
            drawerState: DrawerState.Closed,
            allowCreate: authorizeAction(
                props.userProfile,
                ModuleName.ExpenseGroups,
                ActionType.Create
            ),
        };
    }

    public componentDidMount = () => {
        const {
            match: {
                params: { entityId },
                path,
            },
        } = this.props;

        this.handleRouteUpdate(path, entityId);
    };

    public componentDidUpdate = (prevProps: Props) => {
        const {
            match: {
                params: { entityId },
                path,
            },
        } = this.props;
        if (path !== prevProps.match.path) {
            this.handleRouteUpdate(path, entityId);
        }
    };

    private updateDrawerStateFromPathName = (pathname: string) => {
        switch (pathname) {
            case Routes.ROUTE_EXPENSE_GROUP_NEW:
                this.setState({
                    drawerState: DrawerState.Create,
                });
                return;
            case Routes.ROUTE_EXPENSE_GROUP_EDIT:
                this.setState({
                    drawerState: DrawerState.Edit,
                });
                return;
            case Routes.ROUTE_EXPENSE_GROUP_READ:
                this.setState({
                    drawerState: DrawerState.Read,
                });
                return;
            case Routes.ROUTE_EXPENSE_GROUPS:
                this.setState({
                    drawerState: DrawerState.Closed,
                });
                return;
            default:
                this.setState({
                    drawerState: DrawerState.Closed,
                });
                return;
        }
    };

    private handleRouteUpdate = async (pathname: string, entityId?: string) => {
        let selectedExpenseGroup;
        const client = new ExpenseGroupsClient();

        if (entityId) {
            selectedExpenseGroup = await client.getById(parseInt(entityId, 10));
        }

        this.setState(
            {
                selectedExpenseGroup,
            },
            () => this.updateDrawerStateFromPathName(pathname)
        );
    };

    private handleDrawerMode = (drawerState: DrawerState, id?: string) => {
        const { history } = this.props;

        this.setState({
            drawerState,
        });

        history.push(
            getNewRoute(
                Routes.ROUTE_EXPENSE_GROUPS,
                Routes.ROUTE_EXPENSE_GROUP_READ,
                Routes.ROUTE_EXPENSE_GROUP_NEW,
                drawerState,
                ReplaceStrings.ENTITY_ID,
                id
            )
        );
    };

    private handleSuccessCreate = (_: ExpenseGroupVm) => {
        this.handleDrawerMode(DrawerState.Closed);
        this.tableRef.current.getExpenseGroups();
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const { drawerState, selectedExpenseGroup, allowCreate } = this.state;

        return (
            <>
                <PageHeader
                    title={translations.administration.expenseGroups.title}
                    extra={
                        allowCreate
                            ? [
                                  <Button
                                      key="1"
                                      type="primary"
                                      style={{
                                          zIndex: 10,
                                          float: 'right',
                                      }}
                                      onClick={() => this.handleDrawerMode(DrawerState.Edit)}
                                      className="no-print"
                                  >
                                      <PlusOutlined />
                                      {translations.administration.expenseGroups.add}
                                  </Button>,
                              ]
                            : []
                    }
                />

                <ExpenseGroupTable userProfile={userProfile} wrappedComponentRef={this.tableRef} />

                <Drawer
                    title={
                        drawerState === DrawerState.Create
                            ? translations.administration.expenseGroups.add
                            : translations.administration.expenseGroups.edit
                    }
                    open={!!drawerState}
                    onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    <ExpenseGroupForm
                        onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                        onSuccess={this.handleSuccessCreate}
                        expenseGroup={selectedExpenseGroup}
                    />
                </Drawer>
            </>
        );
    }
}

export default ExpenseGroups;
